import { isPlatformServer } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { BYPASS_ROUTE_GUARD } from '@domgen/dgx-fe-common';
import { PlanClaimsStore } from '@self-fix/data-access';
import {
  MyAccountService,
  PlansResponse,
  LoadingStatus,
  assistanceType,
} from '@self-fix/data-access';
import {
  filter,
  Observable,
  of,
  take,
  tap,
  mergeMap,
  switchMap,
  catchError,
} from 'rxjs';

export const loadPlansGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> => {
  const bypassRoute = inject(BYPASS_ROUTE_GUARD, { optional: true });
  const platformId = inject(PLATFORM_ID);

  if (bypassRoute || isPlatformServer(platformId)) {
    return of(true);
  }
  const queryPlanNumber = route.queryParamMap.get('planNumber');
  const storedPlanNumber = localStorage.getItem('planNumber');

  // reset assistance if stored and query plan numbers are different
  let assistance = 'None';
  if (!queryPlanNumber || queryPlanNumber === storedPlanNumber) {
    assistance = localStorage.getItem('assistance') || 'None';
  }

  return getFromStoreOrAPI(queryPlanNumber, storedPlanNumber, assistance).pipe(
    switchMap(() => of(true)),
    catchError(() => of(false))
  );
};

const getFromStoreOrAPI = (
  queryPlanNumber: string,
  storedPlanNumber: string,
  assistance: string
): Observable<any> => {
  const store: PlanClaimsStore = inject(PlanClaimsStore);
  const accountService: MyAccountService = inject(MyAccountService);
  return store.status$.pipe(
    mergeMap((status: LoadingStatus) => {
      if (status === LoadingStatus.NotLoaded) {
        return accountService.getAllPlans().pipe(
          mergeMap((plans: PlansResponse) => {
            if (plans?.result?.plans && plans?.result?.plans.length) {
              store.setPlans(plans.result.plans);
            }
            const planNumber = queryPlanNumber || storedPlanNumber;

            const activePlan = plans?.result?.plans?.find(
              (plan) => plan.plan_number === planNumber
            );
            if (activePlan) {
              localStorage.setItem('planNumber', planNumber);
              store.setActivePlan(planNumber);
              if (assistance && assistance !== 'None') {
                store.setAssistance(assistance as assistanceType);
              }
              return accountService.getPlanClaims(planNumber).pipe(
                mergeMap((res) => {
                  store.setPlanClaims(res.result);
                  store.setStatus(LoadingStatus.Loaded);
                  return of(LoadingStatus.Loaded);
                }),
                take(1)
              );
            } else {
              localStorage.removeItem('planNumber');
              localStorage.removeItem('assistance');
              store.setStatus(LoadingStatus.Loaded);
              return of(LoadingStatus.Loaded);
            }
          })
        );
      }
      return of(status);
    }),
    filter((status: LoadingStatus) => status === LoadingStatus.Loaded),
    take(1)
  );
};
